import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { CreditBalance } from '@hooks/credit/models/credit-models';

/**
 * Fetches all credit balances for an account
 * (GET /api/v1/ppjk-credits/balances/{accountCode}).
 *
 * `overageBilled`: the account is on a plan invoiced monthly by JARVIS, so a
 * balance below zero is that month's use, billed on the invoice — not a debt
 * to top up. Such an account sees its month's usage instead of a balance.
 */
export default function useAccountBalances(accountCode?: string) {
  const [data, setData] = useState<CreditBalance[]>([]);
  const [isCreditActive, setIsCreditActive] = useState(false);
  const [overageBilled, setOverageBilled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalances = useCallback(
    async (signal?: AbortSignal) => {
      if (!accountCode) {
        setData([]);
        setIsCreditActive(false);
        setOverageBilled(false);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: responseData } = await axiosInstance.get<{
          data: CreditBalance[];
          isCreditActive?: boolean;
          overageBilled?: boolean;
        }>(apiUrl.credit.balance(accountCode), { signal });

        setData(responseData.data || []);
        setIsCreditActive(responseData.isCreditActive === true);
        setOverageBilled(responseData.overageBilled === true);
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }
        const errorMessage = err?.response?.data?.message || 'Gagal mengambil saldo kredit.';
        setError(errorMessage);
        setIsCreditActive(false);
        setOverageBilled(false);
      } finally {
        setLoading(false);
      }
    },
    [accountCode]
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchBalances(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchBalances]);

  return {
    data,
    isCreditActive,
    overageBilled,
    loading,
    error,
    refetch: fetchBalances,
  };
}
